import React, { useRef, useEffect } from "react";
import Button from "components/Form/fields/Button";
import { Field, useFormikContext } from "formik";
import { v4 as uuidv4 } from "uuid";
import { disableSubmit, fieldProps, validEnterKeyPress } from "utils/form";
import { Formik } from "formik";
import startCase from "lodash/startCase";
import classnames from "classnames";
import HoverIconButton from "components/HoverIconButton";
import {
  getStubTemplateLinkProductPrices,
  getStubTemplatePageProductPrices
} from "utils/constants/price";
import { TEMPLATE_LINK_BUILDER, TEMPLATE_PAGE_BUILDER } from "utils/analytics";
import { refocusRef } from "utils/view";

const CONTEXT_STUBS_GETTERS = {
  [TEMPLATE_PAGE_BUILDER]: getStubTemplatePageProductPrices,
  [TEMPLATE_LINK_BUILDER]: getStubTemplateLinkProductPrices
};

const PRODUCT_TEMPLATE = {
  name: "",
  prices: []
};

const NewProductForm = ({ addProducts, placeholder }) => {
  const { values } = useFormikContext();
  const { context } = values.form;
  const getStubPrices = CONTEXT_STUBS_GETTERS[context];

  const name = "name";
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef && inputRef.current.focus();
  }, []);

  return (
    <Formik
      validate={(values) => {
        const errors = {};
        if (!values[name]) {
          errors[name] = `${startCase(name)} is required`;
        }
        return errors;
      }}
      initialValues={PRODUCT_TEMPLATE}
      onSubmit={(values, actions) => {
        if (addProducts) {
          addProducts([
            {
              ...values,
              uuid: uuidv4(),
              prices: getStubPrices()
            }
          ]);
          // Reset back to template initial values
          actions.resetForm();
        }
        actions.setSubmitting(false);
        refocusRef(inputRef);
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        submitForm
      }) => {
        const submitDisabled =
          !values[name] ||
          disableSubmit({
            isSubmitting,
            touched,
            errors
          });

        return (
          <div className="flex flex-column">
            <div className="dib w-100 flex flex-row">
              <Field
                disabled={isSubmitting}
                className="f5 border-box input-reset ba pa2 db w-100 br2 lh-copy b--black-20 br--left"
                innerRef={inputRef}
                {...fieldProps({
                  name,
                  placeholder,
                  setFieldValue,
                  setFieldTouched,
                  onKeyPress: ({ key, currentTarget }) => {
                    !submitDisabled &&
                      validEnterKeyPress({ key, currentTarget }) &&
                      submitForm();
                  }
                })}
              />
              <Button
                type="button"
                disabled={submitDisabled}
                loading={isSubmitting}
                copy={"Add"}
                onClick={submitForm}
                styles={{ height: 42 }}
                classes={{
                  button:
                    "fw6 br2 ph3 pv2 border-box input-reset bg-transparent pointer f6 dib flex-shrink-0 br--right"
                }}
              />
            </div>
            {values[name] && errors[name] && (
              <div className="f6 pt2">{errors[name]}</div>
            )}
          </div>
        );
      }}
    </Formik>
  );
};

const NewProductOption = ({ isLast, isFirst, removeProduct, product }) => {
  const containerProps = {
    className: classnames("pt2 pl2 pr2 pb2 bl br flex flex-row bb hairline-1", {
      "bt br--top br2": isFirst,
      "bb hairline-1": !isLast,
      "br--bottom br2": isLast
    })
  };

  return (
    <div {...containerProps}>
      <div className="flex flex-row justify-between items-center w-100">
        <div className="f5">{product.name}</div>
        <HoverIconButton
          type="close"
          tooltip="Remove product"
          onClick={() => {
            removeProduct(product.uuid);
          }}
        />
      </div>
    </div>
  );
};

const NewProductsForm = () => {
  const { values, setFieldValue } = useFormikContext();
  const products = values.products;
  const addProducts = (newProducts) => {
    const updatedProducts = [...products];

    newProducts.forEach((product) => {
      updatedProducts.push(product);
    });

    setFieldValue("products", updatedProducts);
  };

  const removeProduct = (productUUID) => {
    const updatedProducts = [...products];

    const productIndex = updatedProducts.findIndex(
      ({ uuid }) => uuid == productUUID
    );
    if (productIndex > -1) {
      updatedProducts.splice(productIndex, 1);
    }

    setFieldValue("products", updatedProducts);
  };

  return (
    <div className="ph3">
      {products.length > 0 && (
        <ul className="ma0 pa0 list bg-white pv3">
          {products.map((product, productIx) => {
            const isFirst = !productIx;
            const isLast = products.length - 1 === productIx;
            const optionProps = {
              isFirst,
              isLast,
              product,
              removeProduct
            };
            return <NewProductOption key={productIx} {...optionProps} />;
          })}
        </ul>
      )}
      <NewProductForm
        addProducts={addProducts}
        placeholder="Name your new product"
      />
    </div>
  );
};

export default NewProductsForm;
